<template>
  <div class="token-charge-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">
        <i class="ph-bold ph-arrow-left"></i>
      </button>
      <h1>토큰 충전</h1>
    </div>

    <!-- 현재 보유 토큰 -->
    <div class="balance-card">
      <div class="balance-icon">
        <i class="ph-bold ph-coin"></i>
      </div>
      <div class="balance-info">
        <span class="balance-label">보유 토큰</span>
        <span class="balance-amount">{{ currentBalance.toLocaleString() }}</span>
      </div>
    </div>

    <!-- 충전 패키지 -->
    <div class="section-title">충전 패키지</div>
    <div class="package-grid">
      <div
        v-for="pkg in packages"
        :key="pkg.id"
        class="package-card"
        :class="{ selected: selectedPackage?.id === pkg.id, bonus: pkg.bonus > 0 }"
        @click="selectedPackage = pkg"
      >
        <div class="package-tokens">
          <i class="ph-bold ph-coin"></i>
          <span>{{ pkg.tokens.toLocaleString() }}</span>
        </div>
        <div v-if="pkg.bonus > 0" class="package-bonus">
          +{{ pkg.bonus.toLocaleString() }} 보너스
        </div>
        <div class="package-price">₩{{ pkg.price.toLocaleString() }}</div>
        <div class="package-unit">토큰당 {{ Math.floor(pkg.price / (pkg.tokens + pkg.bonus)) }}원</div>
      </div>
    </div>

    <!-- 결제 버튼 -->
    <div class="charge-action">
      <button
        class="charge-btn"
        :disabled="!selectedPackage || isProcessing"
        @click="handleCharge"
      >
        <template v-if="isProcessing">
          <i class="ph-bold ph-spinner"></i> 결제 중...
        </template>
        <template v-else-if="selectedPackage">
          ₩{{ selectedPackage.price.toLocaleString() }} 결제하기
        </template>
        <template v-else>
          패키지를 선택하세요
        </template>
      </button>
    </div>

    <!-- 충전 내역 -->
    <div class="section-title">충전 내역</div>
    <div class="history-list">
      <div v-if="chargeHistory.length === 0" class="empty-history">
        충전 내역이 없습니다
      </div>
      <div v-for="item in chargeHistory" :key="item.idx" class="history-item">
        <div class="history-info">
          <span class="history-desc">{{ item.reason_detail || item.plan_name || '토큰 충전' }}</span>
          <span class="history-date">{{ formatDate(item.paid_at || item.created_at) }}</span>
        </div>
        <div class="history-amount">
          <span class="history-tokens">+{{ (item.amount || 0).toLocaleString() }}</span>
          <span v-if="item.amount" class="history-price">₩{{ (item.amount || 0).toLocaleString() }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted } from 'vue'

export default {
  name: 'TokenChargePage',
  setup() {
    const purchaseTokens = inject('purchaseTokens')
    const member_info = inject('member_info')
    const sql_query = inject('sql_query')
    const getMbid = () => member_info?.value?.mb_id

    const currentBalance = ref(0)
    const selectedPackage = ref(null)
    const isProcessing = ref(false)
    const chargeHistory = ref([])

    const packages = [
      { id: 1, tokens: 100, bonus: 0, price: 1000 },
      { id: 2, tokens: 500, bonus: 50, price: 4500 },
      { id: 3, tokens: 1000, bonus: 150, price: 8500 },
      { id: 4, tokens: 3000, bonus: 600, price: 23000 },
      { id: 5, tokens: 5000, bonus: 1500, price: 35000 },
      { id: 6, tokens: 10000, bonus: 4000, price: 60000 },
    ]

    async function loadBalance() {
      const mbId = getMbid()
      if (!mbId) return
      try {
        const rows = await sql_query(`SELECT point FROM member WHERE mb_id = '${mbId}'`)
        currentBalance.value = rows?.[0]?.point || 0
      } catch (e) {
        console.warn('잔액 조회 실패:', e)
      }
    }

    async function loadHistory() {
      const mbId = getMbid()
      if (!mbId) return
      try {
        const rows = await sql_query(`
          SELECT * FROM payment_history
          WHERE mb_id = '${mbId}' AND plan_name LIKE '토큰%'
          ORDER BY paid_at DESC LIMIT 20
        `)
        chargeHistory.value = Array.isArray(rows) ? rows : []
      } catch (e) {
        console.warn('충전 내역 조회 실패:', e)
      }
    }

    async function handleCharge() {
      if (!selectedPackage.value || isProcessing.value) return
      isProcessing.value = true

      const pkg = selectedPackage.value
      const totalTokens = pkg.tokens + pkg.bonus

      try {
        const result = await purchaseTokens({
          amount: pkg.price,
          tokenCount: totalTokens,
          customerName: member_info?.value?.mb_name,
          customerEmail: member_info?.value?.email,
          customerPhone: member_info?.value?.mb_hp
        })

        if (result.success) {
          alert(`✅ ${totalTokens.toLocaleString()} 토큰이 충전되었습니다!`)
          currentBalance.value = result.balance || currentBalance.value + totalTokens
          selectedPackage.value = null
          await loadHistory()
        } else {
          alert(`❌ ${result.message}`)
        }
      } catch (e) {
        alert('결제 처리 중 오류가 발생했습니다.')
      } finally {
        isProcessing.value = false
      }
    }

    function formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`
    }

    onMounted(() => {
      // 모바일 리다이렉트 결과 처리
      const params = new URLSearchParams(window.location.search)
      const result = params.get('result')
      if (result === 'success') {
        alert('✅ 토큰이 충전되었습니다!')
        window.history.replaceState({}, '', '/token-charge')
      } else if (result === 'fail') {
        alert(`❌ 결제 실패: ${params.get('message') || ''}`)
        window.history.replaceState({}, '', '/token-charge')
      } else if (result === 'cancel') {
        window.history.replaceState({}, '', '/token-charge')
      }

      loadBalance()
      loadHistory()
    })

    return {
      currentBalance,
      selectedPackage,
      isProcessing,
      chargeHistory,
      packages,
      handleCharge,
      formatDate
    }
  }
}
</script>

<style scoped>
.token-charge-page {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 16px 100px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 10px;
  color: var(--text-primary);
}

.balance-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 28px;
  color: white;
}

.balance-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.balance-info {
  display: flex;
  flex-direction: column;
}

.balance-label {
  font-size: 13px;
  opacity: 0.85;
}

.balance-amount {
  font-size: 28px;
  font-weight: 800;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.package-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 24px;
}

.package-card {
  background: var(--bg-secondary, #f8f9fa);
  border: 2px solid transparent;
  border-radius: 14px;
  padding: 16px 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.package-card:active {
  transform: scale(0.97);
}

.package-card.selected {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.08);
}

.package-tokens {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.package-tokens i {
  color: #f59e0b;
}

.package-bonus {
  font-size: 12px;
  color: #10b981;
  font-weight: 600;
  margin-bottom: 6px;
}

.package-price {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.package-unit {
  font-size: 11px;
  color: var(--text-secondary, #888);
  margin-top: 2px;
}

.charge-action {
  margin-bottom: 32px;
}

.charge-btn {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  color: white;
  background: #6366f1;
  cursor: pointer;
  transition: all 0.2s;
}

.charge-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.charge-btn:not(:disabled):active {
  transform: scale(0.98);
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.empty-history {
  text-align: center;
  padding: 32px;
  color: var(--text-secondary, #888);
  font-size: 14px;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: var(--bg-secondary, #f8f9fa);
  border-radius: 12px;
}

.history-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.history-desc {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.history-date {
  font-size: 12px;
  color: var(--text-secondary, #888);
}

.history-amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.history-tokens {
  font-size: 15px;
  font-weight: 700;
  color: #10b981;
}

.history-price {
  font-size: 12px;
  color: var(--text-secondary, #888);
}
</style>
